import { useState, useEffect, useRef } from "react";
import { FaQuoteLeft, FaPlay, FaPause, FaStar, FaVolumeMute, FaVolumeUp, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../styles/ClientReview.scss";

const ClientReview = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRef = useRef(null);

  const testimonials = [
    {
      src: require("../assets/review vidio/6 Mw Plant Testimonials.mp4"),
      title: "6 MW Plant Testimonial",
      client: "Solar Energy Client",
      rating: 5,
      excerpt: "The installation was seamless and results exceeded our expectations. Outstanding technical expertise and professional service delivery."
    },
    {
      src: require("../assets/review vidio/Gudah kesar Plant Testimonials.mp4"),
      title: "Gudah Kesar Plant Testimonial",
      client: "Industrial Client",
      rating: 5,
      excerpt: "Professional team with exceptional technical knowledge and support. They delivered beyond our expectations with remarkable efficiency."
    },
    {
      src: require("../assets/review vidio/Penchetiya Plant Testimonials.mp4"),
      title: "Penchetiya Plant Testimonial",
      client: "Commercial Client",
      rating: 5,
      excerpt: "Energy costs reduced significantly after implementing their solution. The ROI has been exceptional and the system performs flawlessly."
    },
    {
      src: require("../assets/review vidio/Somesar Plant Testimonials.mp4"),
      title: "Somesar Plant Testimonial",
      client: "Residential Client",
      rating: 5,
      excerpt: "The best investment we made for our home's energy needs. Clean, reliable power with excellent customer support throughout."
    },
  ];

  // Auto play video and change testimonial
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      if (isPlaying) {
        video.play().catch(error => {
          console.log("Auto-play was prevented:", error);
        });
      } else {
        video.pause();
      }

      // Add event listener for when video ends
      const handleVideoEnd = () => {
        // Auto advance to next slide when video ends
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      };

      video.addEventListener('ended', handleVideoEnd);

      return () => {
        video.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, [isPlaying, currentIndex, testimonials.length]);

  // Separate effect for auto-slide functionality when paused
  useEffect(() => {
    let interval;

    // Only auto-advance if video is NOT playing (paused state)
    // This means: when video is playing, no auto-advance
    // When video is paused, auto-advance every 5 seconds
    if (!isPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000); // 5 seconds when paused
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [testimonials.length, isPlaying]);

  const goToSlide = (index) => {
    if (index === currentIndex || isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setIsPlaying(true);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % testimonials.length;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <section className="testimonial-section" aria-labelledby="testimonial-title">
      <div className="testimonial-container">
        <header className="testimonial-header">
          <div className="header-decoration">
            <div className="line"></div>
            <div className="dot"></div>
            <div className="line"></div>
          </div>
          <h2 id="testimonial-title" className="testimonial-title">Client Testimonials</h2>
          <p className="testimonial-subtitle">Hear what our satisfied clients say about our solar solutions</p>
        </header>

        <div className="testimonial-content">
          <div className="testimonial-main">
            <div className="video-container">
              <div className="video-wrapper">
                <video
                  ref={videoRef}
                  src={testimonials[currentIndex].src}
                  muted={isMuted}
                  loop={true}
                  playsInline={true}
                  className="testimonial-video"
                  aria-label={`Client testimonial: ${testimonials[currentIndex].title}`}
                />
                <div className="video-overlay"></div>
                <div className="video-controls">
                  <button
                    className="control-btn play-pause-btn"
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? "Pause video" : "Play video"}
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <button
                    className="control-btn mute-btn"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute video" : "Mute video"}
                  >
                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                </div>
              </div>
            </div>

            <div className="testimonial-info">
              <div className="quote-icon">
                <FaQuoteLeft />
              </div>

              <div className="testimonial-text">
                <p className="testimonial-excerpt">"{testimonials[currentIndex].excerpt}"</p>

                <div className="client-details">
                  <h3 className="client-name">{testimonials[currentIndex].title}</h3>
                  <p className="client-type">{testimonials[currentIndex].client}</p>

                  <div className="rating">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <FaStar key={i} className="star" />
                    ))}
                    <span className="rating-text">({testimonials[currentIndex].rating}.0)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <button className="nav-arrow nav-arrow-left" onClick={prevSlide} aria-label="Previous testimonial">
            <FaChevronLeft />
          </button>
          <button className="nav-arrow nav-arrow-right" onClick={nextSlide} aria-label="Next testimonial">
            <FaChevronRight />
          </button>
        </div>

        <div className="testimonial-nav">
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`nav-dot ${i === currentIndex ? "active" : ""}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              aria-current={i === currentIndex ? "true" : "false"}
              style={{
                // Fallback styles in case SCSS doesn't load
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid rgba(0, 71, 171, 0.4)',
                background: i === currentIndex ? 'rgba(0, 71, 171, 0.3)' : 'rgba(0, 71, 171, 0.2)',
                cursor: 'pointer',
                margin: '0 6px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span
                className="dot-inner"
                style={{
                  // Fallback styles for inner dot
                  width: i === currentIndex ? '8px' : '6px',
                  height: i === currentIndex ? '8px' : '6px',
                  borderRadius: '50%',
                  background: i === currentIndex ? '#0047AB' : 'rgba(0, 71, 171, 0.5)',
                  display: 'block',
                  transition: 'all 0.3s ease'
                }}
              ></span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientReview;